"use client";

import { loginAction } from "@/actions/login-action";
import { type LoginFormSchema, loginFormSchema } from "@/actions/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@trendmap/ui/components/alert";
import { Button } from "@trendmap/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@trendmap/ui/components/form";
import { Input } from "@trendmap/ui/components/input";
import { AlertTriangleIcon, Loader2Icon } from "lucide-react";
import { useAction } from "next-safe-action/hooks";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Logo } from "../logo";
import { MagicLinkSent } from "../magic-link-sent";

export function LoginForm() {
  const [magicLinkSent, setMagicLinkSent] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const { execute, isExecuting } = useAction(loginAction, {
    onSuccess: () => {
      setError(false);
      setMagicLinkSent(true);
    },
    onError: () => {
      setError(true);
      setMagicLinkSent(false);
    },
  });

  const form = useForm<LoginFormSchema>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: "",
    },
  });

  const email = form.watch("email");

  const onSubmit = form.handleSubmit((data) => {
    execute(data);
  });

  return (
    <div className="grid gap-4 md:w-2/3">
      <div className="w-full px-4 py-15">
        <div className="flex gap-4 items-center justify-center mb-10 lg:mb-20">
          <Logo />
          <div className="bg-gray-200 dark:bg-accent dark:text-white text-xs px-4 py-2 uppercase leading-none tracking-widest">
            Admin
          </div>
        </div>

        {error ? (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangleIcon className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>Invalid credentials</AlertDescription>
          </Alert>
        ) : null}

        {magicLinkSent ? (
          <MagicLinkSent email={email} />
        ) : (
          <Form {...form}>
            <form onSubmit={onSubmit}>
              <div className="grid gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input autoCapitalize="off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={isExecuting}>
                  {isExecuting ? (
                    <Loader2Icon className="h-4 w-4 animate-spin" />
                  ) : (
                    "Send Magic Link"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </div>
    </div>
  );
}
